import { Vue, Component, Ref, Emit, Prop } from 'vue-property-decorator'
import {
    initialStatisticalAnalysisViewDetailTableGroupingState,
    StatisticalAnalysisViewDetailTableGroupingGridItem,
    StatisticalAnalysisViewDetailTableGroupingState
} from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table-grouping/statistical-analysis-view-detail-table-grouping.model'
import { BModal } from 'bootstrap-vue'
import { GridLayout, GridItem } from 'vue-grid-layout'
import { cloneDeep, concat, isArray, orderBy, uniqueId } from 'lodash'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'
import { statisticalAnalysisGroupingService } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.service'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDossiers, StatisticalAnalysisDossiersExternalInfo } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { getIconHeaders } from '@/modules/statistical-analysis-module/shared/helpers/helpers'

@Component({
    components: {
        GridLayout,
        GridItem
    }
})
export default class StatisticalAnalysisViewDetailTableGrouping extends Vue {
    @Prop() readonly dossierSelected!: StatisticalAnalysisDossiers;
    @Prop() readonly externalInfo!: StatisticalAnalysisDossiersExternalInfo
    @Ref() readonly statisticAnalysisGroupingModal!: BModal
    @Emit()
    selectionFields(selectedFields: StatisticalAnalysisGroupingResponseField[]): StatisticalAnalysisGroupingResponseField[] {
        return selectedFields
    }

    state: StatisticalAnalysisViewDetailTableGroupingState
    constructor() {
        super()
        this.state = initialStatisticalAnalysisViewDetailTableGroupingState()
    }

    get isLoading(): boolean {
        return this.state.loading
    }

    get isExternalLoading(): boolean {
        return this.state.externalLoading
    }

    get translationNumberGrouping(): number {
        return this.numberGrouping > 1 ? 2 : 1
    }

    get numberGrouping(): number {
        return this.state.fieldsSelected.length
    }

    get getLayout(): StatisticalAnalysisViewDetailTableGroupingGridItem[] {
        return this.state.temporalSelected
    }

    get canUnselect(): boolean {
        return this.state.temporalSelected.length > 1
    }

    async created(): Promise<void> {
        this.$eventHub.$on('statistical-loading', this._onStatisticalLoadingGridTable)
        await this.getFieldsGrouping()
    }

    beforeDestroy() {
        this.$eventHub.$off('statistical-loading', this._onStatisticalLoadingGridTable)
    }

    onApply(): void {
        this.state.fields = cloneDeep(this.state.temporalFields)
        this.state.fieldsSelected = cloneDeep(this.state.temporalSelected)
        this._getOrderedSelectedFields()
        this.onHide()
    }

    onHide(): void {
        setTimeout(() => {
            this.state.temporalFields = cloneDeep(this.state.fields)
            this.state.temporalSelected = cloneDeep(this.state.fieldsSelected)
        }, 200)
        this.statisticAnalysisGroupingModal.hide()
    }

    onOpenModal(): void {
        this.statisticAnalysisGroupingModal.show()
    }

    onChangeAvailableField(item: StatisticalAnalysisGroupingResponseField, indexItem: number): void {
        this.state.temporalFields.splice(indexItem, 1)
        this.state.temporalSelected.push(this._transformToSelectedFields(item))
    }

    onChangeSelectedField(item: StatisticalAnalysisViewDetailTableGroupingGridItem, indexItem: number): void {
        this.state.temporalSelected.splice(indexItem, 1)
        this.state.temporalFields.push(this._transformToAvailableFields(item))
    }

    getIconGrouping(entity: StatiscalAnalysisTypeCases): string {
        return getIconHeaders(entity)
    }

    private _transformToSelectedFields(item: StatisticalAnalysisGroupingResponseField): StatisticalAnalysisViewDetailTableGroupingGridItem {
        if (this.state.fieldsSelected.length) {
            return {
                fieldName: item.fieldName,
                translatedFieldName: item.translatedFieldName,
                entity: item.entity,
                x: 0,
                y: this.state.fieldsSelected[this.state.fieldsSelected.length - 1].y + 1,
                h: 1,
                i: uniqueId(),
                w: 1
            }
        } else {
            return {
                fieldName: item.fieldName,
                translatedFieldName: item.translatedFieldName,
                entity: item.entity,
                x: 0,
                y: 0,
                h: 1,
                i: uniqueId(),
                w: 1
            }
        }
    }

    private _transformToAvailableFields(item: StatisticalAnalysisViewDetailTableGroupingGridItem): StatisticalAnalysisGroupingResponseField {
        return {
            fieldName: item.fieldName,
            translatedFieldName: item.translatedFieldName,
            entity: item.entity
        }
    }

    async getFieldsGrouping(): Promise<void> {
        this.state.loading = true
        let typeSearch
        if (isArray(this.externalInfo.typeSearch)) {
            typeSearch = (this.externalInfo.typeSearch).map((item: StatisticalAnalysisSidebarItemCombo) => item.id) as StatiscalAnalysisTypeCases[]
        } else {
            typeSearch = [this.externalInfo.typeSearch?.id as StatiscalAnalysisTypeCases]
        }
        const [error, response] = await this.$_toCall(statisticalAnalysisGroupingService.getFieldsGrouping(this.dossierSelected, typeSearch))
        if (error) {
            this.state.fields = []
            this.state.fieldsSelected = []
            this.state.temporalFields = []
            this.state.temporalSelected = []
        } else {
            this.state.fields = response.availableFields
            this.state.fieldsSelected = response.selectedFields.map((item: StatisticalAnalysisGroupingResponseField) => this._transformToSelectedFields(item))
            this.state.temporalFields = cloneDeep(this.state.fields)
            this.state.temporalSelected = cloneDeep(this.state.fieldsSelected)
        }
        this._getOrderedSelectedFields()
        this.$eventHub.$emit('statistical-grouping-loaded', concat([], response.availableFields, response.selectedFields))
        this.state.loading = false
    }

    private _getOrderedSelectedFields(): void {
        const orderedSelectionFields: StatisticalAnalysisViewDetailTableGroupingGridItem[] = orderBy(this.state.fieldsSelected, ['y'], ['asc'])
        const selectionFields:StatisticalAnalysisGroupingResponseField[] = orderedSelectionFields.map((item: StatisticalAnalysisViewDetailTableGroupingGridItem) => this._transformToAvailableFields(item))
        this.selectionFields(selectionFields)
    }

    private _onStatisticalLoadingGridTable(value: boolean): void {
        this.state.externalLoading = value
    }
}
